import dayjs from 'dayjs';

const FALLBACK_TZ = 'Asia/Hong_Kong';

/**
 * Read the company timezone (IANA) stored in localStorage by WpaLayout.
 * Falls back to Asia/Hong_Kong when not set.
 */
export function getWpaCompanyTimezone() {
  try {
    const tz = localStorage.getItem('wpa_company_timezone');
    if (tz) {
      // Quick sanity-check: Intl must recognise the ID.
      Intl.DateTimeFormat(undefined, { timeZone: tz });
      return tz;
    }
  } catch {
    // Invalid IANA id — fall through to default.
  }
  return FALLBACK_TZ;
}

/**
 * Format a UTC ISO date-time string (or Date) into the company's local
 * wall-clock representation.
 *
 * Supported format tokens (subset, same as dayjs):
 *   YYYY  MM  DD  HH  mm  ss
 *
 * @param {string|Date|null|undefined} isoStr  UTC datetime value
 * @param {string} timezone                    IANA timezone, e.g. 'Asia/Hong_Kong'
 * @param {string} [format='YYYY-MM-DD']       Output format
 * @returns {string}
 */
export function formatWpaDate(isoStr, timezone, format = 'YYYY-MM-DD') {
  if (!isoStr) return '-';

  let tz = timezone || getWpaCompanyTimezone();
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
  } catch {
    tz = FALLBACK_TZ;
  }

  let date;
  try {
    date = typeof isoStr === 'string'
      ? new Date(isoStr.endsWith('Z') || isoStr.includes('+') ? isoStr : isoStr + 'Z')
      : isoStr instanceof Date ? isoStr : new Date(isoStr);
    if (isNaN(date.getTime())) return String(isoStr).slice(0, 10);
  } catch {
    return String(isoStr).slice(0, 10);
  }

  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(date);

    const get = (type) => parts.find((p) => p.type === type)?.value ?? '00';
    const Y = get('year');
    const M = get('month');
    const D = get('day');
    const h = get('hour') === '24' ? '00' : get('hour');
    const m = get('minute');
    const s = get('second');

    return format
      .replace('YYYY', Y)
      .replace('MM', M)
      .replace('DD', D)
      .replace('HH', h)
      .replace('mm', m)
      .replace('ss', s);
  } catch {
    return String(isoStr).slice(0, 10);
  }
}

/**
 * Return today's date string (YYYY-MM-DD) in the given IANA timezone.
 * Uses the server UTC clock via `new Date()`.
 *
 * @param {string} [timezone]  IANA timezone (defaults to wpa_company_timezone)
 * @returns {string}  e.g. '2026-05-21'
 */
export function getCompanyTodayStr(timezone) {
  return formatWpaDate(new Date(), timezone || getWpaCompanyTimezone(), 'YYYY-MM-DD');
}

/**
 * Return a dayjs object representing the start of today in the company's
 * timezone.  Drop-in replacement for `dayjs().startOf('day')` in
 * `wpaDeliveryAvailability.js`.
 *
 * @param {string} [timezone]  IANA timezone (defaults to wpa_company_timezone)
 * @returns {dayjs.Dayjs}
 */
export function getCompanyToday(timezone) {
  return dayjs(getCompanyTodayStr(timezone));
}
